import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { fetchRecentParentMessages, getStudentByEmail, ParentMessage } from '../../services/parentMessageService';
import { formatTime } from '../../lib/dateUtils';

interface NotificationBellProps {
  className?: string;
}

interface MessageWithUser extends ParentMessage {
  studentName?: string;
  fatherName?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<MessageWithUser[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load parent users and recent messages
  const loadData = async () => {
    setLoading(true);
    try {
      const recentMessages = await fetchRecentParentMessages(5);

      // Enrich messages with user information by fetching student data for each message
      const enrichedMessages = await Promise.all(
        recentMessages.map(async (message) => {
          const studentInfo = await getStudentByEmail(message.userId);
          return {
            ...message,
            studentName: studentInfo?.studentName || 'Unknown',
            fatherName: studentInfo?.fatherName || 'Unknown'
          };
        })
      );

      setMessages(enrichedMessages);
    } catch (error) {
      console.error('Error loading notification data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load data when component mounts
  useEffect(() => {
    loadData();
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      loadData(); // Refresh data when opening
    }
  };

  const truncateMessage = (message: string, maxLength: number = 50) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Bell Icon */}
      <button
        onClick={toggleDropdown}
        className="relative p-2 text-gray-500 hover:text-gray-700 transition-colors"
      >
        <Bell size={20} />
        {messages.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {messages.length}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Recent Parent Messages</h3>
            <p className="text-sm text-gray-600">Latest messages from parents</p>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-2">Loading messages...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <Bell size={32} className="mx-auto mb-2 text-gray-300" />
                <p>No recent messages from parents</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {messages.map((message) => (
                  <div key={message.$id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start space-x-3">
                      {/* Avatar */}
                      <div className="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                        {message.fatherName?.charAt(0).toUpperCase() || 'P'}
                      </div>

                      {/* Message Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">
                            {message.fatherName || 'Unknown Parent'}
                          </p>
                          <span className="text-xs text-gray-500">
                            {formatTime(message.time)}
                          </span>
                        </div>
                        
                        <p className="text-xs text-gray-500 mb-1">
                          Parent of {message.studentName || 'Unknown Student'}
                        </p>

                        {/* Message preview */}
                        <div className="mt-1">
                          {message.img && (
                            <div className="flex items-center text-xs text-gray-500 mb-1">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              📷 Image
                            </div>
                          )}
                          {message.message && (
                            <p className="text-sm text-gray-700">
                              {truncateMessage(message.message)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => {
                setIsOpen(false);
                // Navigate to parent chat page - you might want to use useNavigate here
                window.location.href = '/parent-chat';
              }}
              className="w-full text-sm text-pink-600 hover:text-pink-700 font-medium transition-colors"
            >
              View All Messages
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
